import { defineComponent, PropType } from "@vue/composition-api";
import { CreateElement, RenderContext, VNode } from "vue";
import { TranslateResult } from "vue-i18n";
import { isIOS } from "@/lib/browserUtils";
import BaseSpinner from "@/components/BaseSpinner";

interface Props {
  src: string;
  alt: string;
  topRightIcon: boolean;
  topLeftIcon: boolean;
  centerIcon: boolean;
  bottomLeftIcon: boolean;
  bottomRightIcon: boolean;
  lazyLoading: boolean;
  loading: boolean;
  overlay: boolean;
  isIOS: () => boolean;
}

export default defineComponent({
  name: "BaseImage",
  functional: true,
  props: {
    src: { type: String, required: true },
    alt: { type: String as () => TranslateResult | string },
    topRightIcon: { type: Boolean, default: false },
    topLeftIcon: { type: Boolean, default: false },
    centerIcon: { type: Boolean, default: false },
    bottomLeftIcon: { type: Boolean, default: false },
    bottomRightIcon: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    overlay: { type: Boolean, default: false },
    lazyLoading: { type: Boolean, default: true },
    isIOS: { type: Function as PropType<() => boolean>, default: isIOS },
  },
  render(h: CreateElement, ctx: RenderContext<Props>): VNode {
    const isBlob = ctx.props.src.startsWith("blob:");
    const isIOS = ctx.props.isIOS();
    const src = ctx.props.src;
    const alt = ctx.props.alt;

    const directives = ctx.props.lazyLoading ? (!isBlob ? [{ name: "lazy-load" }] : []) : undefined;
    const getSrcSettings = () => (ctx.props.lazyLoading ? { [isBlob ? "src" : "data-src"]: src } : { src: src });

    return h(
      "div",
      {
        staticClass: "base-image",
        class: [...(Array.isArray(ctx.data.class) ? ctx.data.class : [ctx.data.class]), ctx.data.staticClass],
        style: [...(Array.isArray(ctx.data.style) ? ctx.data.style : [ctx.data.style]), ctx.data.staticStyle],
        on: {
          ...(ctx.listeners.click ? { click: ctx.listeners.click } : {}),
        },
      },
      [
        h(
          "picture",
          {
            directives: directives,
          },
          [
            h("img", {
              staticClass: "base-image__image",
              attrs: {
                ...getSrcSettings(),
                alt,
                loading: "lazy",
              },
              on: {
                drag: (event: MouseEvent): void => event.preventDefault(),
                dragdrop: (event: MouseEvent): void => event.preventDefault(),
                dragstart: (event: MouseEvent): void => event.preventDefault(),
                contextmenu: (event: MouseEvent): void => event.preventDefault(),
              },
              directives: [...(isBlob && !isIOS ? [{ name: "img-orientation-changer" }] : [])],
            }),
          ]
        ),
        ctx.props.overlay && h("div", { staticClass: "base-image__overlay" }),
        ctx.props.loading &&
          h(
            "div",
            {
              staticClass: "loading-box d-center",
              staticStyle: { position: "absolute" },
            },
            [h(BaseSpinner)]
          ),
        ctx.props.topLeftIcon &&
          h(
            "div",
            {
              staticClass: "base-image__icon __top-left d-center",
              on: {
                ...(ctx.listeners["click:top-left"] ? { click: ctx.listeners["click:top-left"] } : {}),
              },
            },
            ctx.scopedSlots["top-left"] && ctx.scopedSlots["top-left"]({})
          ),
        ctx.props.topRightIcon &&
          h(
            "div",
            {
              staticClass: "base-image__icon __top-right d-center",
              on: {
                ...(ctx.listeners["click:top-right"] ? { click: ctx.listeners["click:top-right"] } : {}),
              },
            },
            ctx.scopedSlots["top-right"] && ctx.scopedSlots["top-right"]({})
          ),
        ctx.props.centerIcon &&
          h(
            "div",
            {
              staticClass: "base-image__icon __center",
              on: {
                ...(ctx.listeners["click:center"] ? { click: ctx.listeners["click:center"] } : {}),
              },
            },
            ctx.scopedSlots["center"] && ctx.scopedSlots["center"]({})
          ),
        ctx.props.bottomLeftIcon &&
          h(
            "div",
            {
              staticClass: "base-image__icon __bottom-left d-center",
              on: {
                ...(ctx.listeners["click:bottom-left"] ? { click: ctx.listeners["click:bottom-left"] } : {}),
              },
            },
            ctx.scopedSlots["bottom-left"] && ctx.scopedSlots["bottom-left"]({})
          ),
        ctx.props.bottomRightIcon &&
          h(
            "div",
            {
              staticClass: "base-image__icon __bottom-right d-center",
              on: {
                ...(ctx.listeners["click:bottom-right"] ? { click: ctx.listeners["click:bottom-right"] } : {}),
              },
            },
            ctx.scopedSlots["bottom-right"] && ctx.scopedSlots["bottom-right"]({})
          ),
      ]
    );
  },
});
